import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Plus, DollarSign, History, Trash2, Pencil, Wallet, HandCoins, Filter, FileDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status pill colors
const STATUS_STYLES = {
  open: { label: 'Open', cls: 'bg-red-600 hover:bg-red-600' },
  partial: { label: 'Partial', cls: 'bg-amber-500 hover:bg-amber-500' },
  paid_in_full: { label: 'Paid In Full', cls: 'bg-emerald-600 hover:bg-emerald-600' },
};

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'square', label: 'Square' },
  { value: 'transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
];

const todayISO = () => new Date().toISOString().split('T')[0];
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function FinancialAssistanceLedger() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [summary, setSummary] = useState({ total_lent: 0, total_paid: 0, total_outstanding: 0, total_entries: 0 });
  const [members, setMembers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Create / Edit dialog
  const [entryDialog, setEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // null = create mode
  const [entryForm, setEntryForm] = useState({
    member_id: '',
    date_received: todayISO(),
    amount_received: '',
    reason: '',
    notes: '',
  });

  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentEntry, setPaymentEntry] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    date: todayISO(),
    amount: '',
    method: 'cash',
    notes: '',
  });

  // Detail dialog
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const downloadPdf = async (params, filename) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/financial-assistance/export/pdf`, {
        ...authHeader,
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/financial-assistance`, authHeader),
        axios.get(`${BACKEND_URL}/api/financial-assistance/summary`, authHeader),
      ]);
      setEntries(listRes.data?.entries || []);
      setCanManage(!!listRes.data?.can_manage);
      setSummary(sumRes.data || {});
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("You don't have permission to view the Financial Assistance ledger.");
        navigate('/');
      } else {
        toast.error('Failed to load Financial Assistance ledger');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  const loadMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(res.data) ? res.data : res.data?.members || [];
      // Sort by handle for the picker
      list.sort((a, b) => (a.handle || '').localeCompare(b.handle || ''));
      setMembers(list);
    } catch {
      /* non-fatal */
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refresh();
      await loadMembers();
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh, loadMembers]);

  // ---------- Create / Edit ----------
  const openCreateDialog = () => {
    setEditingEntry(null);
    setEntryForm({
      member_id: '',
      date_received: todayISO(),
      amount_received: '',
      reason: '',
      notes: '',
    });
    setEntryDialog(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      member_id: entry.member_id,
      date_received: entry.date_received,
      amount_received: String(entry.amount_received),
      reason: entry.reason || '',
      notes: entry.notes || '',
    });
    setEntryDialog(true);
  };

  const submitEntry = async () => {
    const amt = parseFloat(entryForm.amount_received);
    if (!entryForm.member_id) return toast.error('Select a member');
    if (!entryForm.date_received) return toast.error('Pick a date received');
    if (!amt || amt <= 0) return toast.error('Amount must be greater than 0');
    try {
      if (editingEntry) {
        await axios.patch(
          `${BACKEND_URL}/api/financial-assistance/${editingEntry.id}`,
          {
            date_received: entryForm.date_received,
            amount_received: amt,
            reason: entryForm.reason,
            notes: entryForm.notes,
          },
          authHeader
        );
        toast.success('Ledger entry updated');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/financial-assistance`,
          {
            member_id: entryForm.member_id,
            date_received: entryForm.date_received,
            amount_received: amt,
            reason: entryForm.reason,
            notes: entryForm.notes,
          },
          authHeader
        );
        toast.success('Ledger entry created');
      }
      setEntryDialog(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save entry');
    }
  };

  // ---------- Payments ----------
  const openPaymentDialog = (entry) => {
    setPaymentEntry(entry);
    setPaymentForm({ date: todayISO(), amount: '', method: 'cash', notes: '' });
    setPaymentDialog(true);
  };

  const submitPayment = async () => {
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) return toast.error('Amount must be greater than 0');
    if (!paymentForm.date) return toast.error('Pick a payment date');
    try {
      await axios.post(
        `${BACKEND_URL}/api/financial-assistance/${paymentEntry.id}/payments`,
        {
          date: paymentForm.date,
          amount: amt,
          method: paymentForm.method,
          notes: paymentForm.notes,
        },
        authHeader
      );
      toast.success('Payment recorded');
      setPaymentDialog(false);
      refresh();
      // If the detail dialog was open, refresh its data too
      if (detailEntry?.id === paymentEntry.id) {
        const fresh = await axios.get(
          `${BACKEND_URL}/api/financial-assistance/${paymentEntry.id}`,
          authHeader
        );
        setDetailEntry(fresh.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record payment');
    }
  };

  const reversePayment = async (entryId, paymentId) => {
    if (!window.confirm('Reverse this payment? This will be recorded in the history.')) return;
    try {
      const res = await axios.delete(
        `${BACKEND_URL}/api/financial-assistance/${entryId}/payments/${paymentId}`,
        authHeader
      );
      toast.success('Payment reversed');
      setDetailEntry(res.data);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reverse payment');
    }
  };

  // ---------- Delete ----------
  const deleteEntry = async (entry) => {
    if (
      !window.confirm(
        `Delete ledger entry for ${entry.member?.handle || 'this member'}? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/financial-assistance/${entry.id}`, authHeader);
      toast.success('Entry deleted');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete entry');
    }
  };

  // ---------- Detail ----------
  const openDetailDialog = async (entry) => {
    setDetailEntry(entry);
    setDetailDialog(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/financial-assistance/${entry.id}`,
        authHeader
      );
      setDetailEntry(res.data);
    } catch {
      /* fallback to list version */
    }
  };

  // ---------- Filtering ----------
  const filteredEntries = entries.filter((e) => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const handle = (e.member?.handle || '').toLowerCase();
      const name = (e.member?.real_name || '').toLowerCase();
      const reason = (e.reason || '').toLowerCase();
      if (!handle.includes(s) && !name.includes(s) && !reason.includes(s)) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-900 text-white p-4 sm:p-6" data-testid="financial-assistance-page">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/officer-tracking')}
              className="border-slate-600 bg-slate-800 hover:bg-slate-700 text-white"
              data-testid="fa-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to A&D
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                Financial Assistance Ledger
              </h1>
              <p className="text-xs sm:text-sm text-slate-400">
                National-only · Track members who received assistance and their repayment progress
              </p>
            </div>
          </div>

          {canManage && (
            <Button
              onClick={openCreateDialog}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="fa-new-entry-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Entry
            </Button>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryCard
            icon={<HandCoins className="w-5 h-5 text-emerald-400" />}
            label="Total Lent"
            value={money(summary.total_lent)}
            sub={`${summary.total_entries || 0} entries`}
            testId="fa-summary-lent"
          />
          <SummaryCard
            icon={<DollarSign className="w-5 h-5 text-blue-400" />}
            label="Total Repaid"
            value={money(summary.total_paid)}
            testId="fa-summary-paid"
          />
          <SummaryCard
            icon={<Wallet className="w-5 h-5 text-amber-400" />}
            label="Outstanding"
            value={money(summary.total_outstanding)}
            sub={`${summary.open_count || 0} open · ${summary.partial_count || 0} partial`}
            testId="fa-summary-outstanding"
          />
          <SummaryCard
            icon={<DollarSign className="w-5 h-5 text-emerald-400" />}
            label="Paid In Full"
            value={summary.paid_count || 0}
            sub="completed entries"
            testId="fa-summary-paid-count"
          />
        </div>

        {/* Filter bar */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="flex-1">
              <Input
                placeholder="Search by member, name, or reason..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="fa-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="w-44 bg-slate-700 border-slate-600 text-white"
                  data-testid="fa-status-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid_in_full">Paid In Full</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Ledger table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base">Ledger Entries</CardTitle>
                <CardDescription className="text-slate-400">
                  {filteredEntries.length} entr{filteredEntries.length === 1 ? 'y' : 'ies'}
                </CardDescription>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadPdf(
                  statusFilter !== 'all' ? { status: statusFilter } : {},
                  'financial-assistance-ledger.pdf'
                )}
                className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                data-testid="fa-export-pdf-all"
              >
                <FileDown className="w-3.5 h-3.5 mr-1" /> Export PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading…</div>
            ) : filteredEntries.length === 0 ? (
              <div className="p-8 text-center text-slate-400" data-testid="fa-empty-state">
                No ledger entries{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Member</TableHead>
                        <TableHead className="text-slate-400">Date Received</TableHead>
                        <TableHead className="text-slate-400">Amount</TableHead>
                        <TableHead className="text-slate-400">Repaid</TableHead>
                        <TableHead className="text-slate-400">Balance</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Reason</TableHead>
                        <TableHead className="text-slate-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEntries.map((entry) => {
                        const sty = STATUS_STYLES[entry.status] || STATUS_STYLES.open;
                        return (
                          <TableRow
                            key={entry.id}
                            className="border-slate-700"
                            data-testid={`fa-row-${entry.id}`}
                          >
                            <TableCell className="font-medium text-white">
                              {entry.member?.handle || '—'}
                              <div className="text-xs text-slate-400">{entry.member?.chapter}</div>
                            </TableCell>
                            <TableCell className="text-slate-300">{entry.date_received}</TableCell>
                            <TableCell className="text-white">{money(entry.amount_received)}</TableCell>
                            <TableCell className="text-emerald-400">{money(entry.paid_to_date)}</TableCell>
                            <TableCell
                              className={
                                entry.balance > 0 ? 'text-amber-400' : 'text-slate-400'
                              }
                            >
                              {money(entry.balance)}
                            </TableCell>
                            <TableCell>
                              <Badge className={`${sty.cls} text-white`}>{sty.label}</Badge>
                            </TableCell>
                            <TableCell className="text-slate-300 max-w-[200px] truncate">
                              {entry.reason || '—'}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openDetailDialog(entry)}
                                className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                                data-testid={`fa-view-${entry.id}`}
                              >
                                <History className="w-3 h-3 mr-1" /> History
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadPdf(
                                  { entry_id: entry.id },
                                  `assistance-${entry.member?.handle || 'entry'}-${entry.date_received}.pdf`
                                )}
                                className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                                data-testid={`fa-pdf-${entry.id}`}
                                title="Download this entry as PDF"
                              >
                                <FileDown className="w-3 h-3" />
                              </Button>
                              {canManage && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => openPaymentDialog(entry)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                    data-testid={`fa-pay-${entry.id}`}
                                    disabled={entry.status === 'paid_in_full'}
                                  >
                                    <DollarSign className="w-3 h-3 mr-1" /> Pay
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditDialog(entry)}
                                    className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                                    data-testid={`fa-edit-${entry.id}`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => deleteEntry(entry)}
                                    className="border-red-700 bg-red-900/40 hover:bg-red-900/60 text-red-300"
                                    data-testid={`fa-delete-${entry.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3 p-3">
                  {filteredEntries.map((entry) => {
                    const sty = STATUS_STYLES[entry.status] || STATUS_STYLES.open;
                    return (
                      <div
                        key={entry.id}
                        className="bg-slate-700/50 rounded-lg p-3 border border-slate-600"
                        data-testid={`fa-card-${entry.id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-white">{entry.member?.handle || '—'}</div>
                            <div className="text-xs text-slate-400">
                              {entry.member?.chapter} · {entry.date_received}
                            </div>
                          </div>
                          <Badge className={`${sty.cls} text-white`}>{sty.label}</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                          <div>
                            <div className="text-slate-400">Amount</div>
                            <div className="text-white font-semibold">{money(entry.amount_received)}</div>
                          </div>
                          <div>
                            <div className="text-slate-400">Repaid</div>
                            <div className="text-emerald-400 font-semibold">
                              {money(entry.paid_to_date)}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-400">Balance</div>
                            <div
                              className={
                                entry.balance > 0
                                  ? 'text-amber-400 font-semibold'
                                  : 'text-slate-300 font-semibold'
                              }
                            >
                              {money(entry.balance)}
                            </div>
                          </div>
                        </div>
                        {entry.reason && (
                          <div className="text-xs text-slate-300 mb-2 truncate">
                            <span className="text-slate-400">Reason: </span>
                            {entry.reason}
                          </div>
                        )}
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetailDialog(entry)}
                            className="flex-1 text-xs h-8 border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                          >
                            History
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadPdf(
                              { entry_id: entry.id },
                              `assistance-${entry.member?.handle || 'entry'}-${entry.date_received}.pdf`
                            )}
                            className="text-xs h-8 border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                            data-testid={`fa-pdf-card-${entry.id}`}
                          >
                            <FileDown className="w-3 h-3" />
                          </Button>
                          {canManage && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => openPaymentDialog(entry)}
                                className="flex-1 text-xs h-8 bg-blue-600 hover:bg-blue-700"
                                disabled={entry.status === 'paid_in_full'}
                              >
                                Pay
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditDialog(entry)}
                                className="text-xs h-8 border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteEntry(entry)}
                                className="text-xs h-8 border-red-700 bg-red-900/40 hover:bg-red-900/60 text-red-300"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={entryDialog} onOpenChange={setEntryDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Ledger Entry' : 'New Ledger Entry'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingEntry
                ? 'Edits are tracked in the entry history.'
                : 'Record a new financial assistance grant.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editingEntry && (
              <div>
                <Label>Member</Label>
                <Select
                  value={entryForm.member_id}
                  onValueChange={(v) => setEntryForm({ ...entryForm, member_id: v })}
                >
                  <SelectTrigger
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="fa-form-member"
                  >
                    <SelectValue placeholder="Select a member" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.handle} {m.chapter ? `(${m.chapter})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Date Received</Label>
              <Input
                type="date"
                value={entryForm.date_received}
                onChange={(e) => setEntryForm({ ...entryForm, date_received: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="fa-form-date"
              />
            </div>
            <div>
              <Label>Amount Received ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={entryForm.amount_received}
                onChange={(e) => setEntryForm({ ...entryForm, amount_received: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="fa-form-amount"
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                placeholder="Medical, hardship, travel, …"
                value={entryForm.reason}
                onChange={(e) => setEntryForm({ ...entryForm, reason: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="fa-form-reason"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={entryForm.notes}
                onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="fa-form-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEntryDialog(false)}
              className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={submitEntry}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="fa-form-submit"
            >
              {editingEntry ? 'Save changes' : 'Create entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription className="text-slate-400">
              {paymentEntry?.member?.handle} · balance{' '}
              <span className="text-amber-400">{money(paymentEntry?.balance)}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentForm.date}
                onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="fa-pay-date"
              />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="fa-pay-amount"
              />
            </div>
            <div>
              <Label>Method</Label>
              <Select
                value={paymentForm.method}
                onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}
              >
                <SelectTrigger
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="fa-pay-method"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={2}
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="fa-pay-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPaymentDialog(false)}
              className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={submitPayment}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="fa-pay-submit"
            >
              Record payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / History Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Entry Detail · {detailEntry?.member?.handle || '—'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {detailEntry?.date_received} · {money(detailEntry?.amount_received)} received ·{' '}
              {money(detailEntry?.balance)} balance
            </DialogDescription>
          </DialogHeader>
          {detailEntry && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto">
              {detailEntry.reason && (
                <div>
                  <Label className="text-slate-400">Reason</Label>
                  <div className="text-white text-sm">{detailEntry.reason}</div>
                </div>
              )}
              {detailEntry.notes && (
                <div>
                  <Label className="text-slate-400">Notes</Label>
                  <div className="text-white text-sm whitespace-pre-wrap">{detailEntry.notes}</div>
                </div>
              )}
              <div>
                <Label className="text-slate-400">Payments ({detailEntry.payments?.length || 0})</Label>
                {(!detailEntry.payments || detailEntry.payments.length === 0) ? (
                  <div className="text-slate-500 text-sm italic">No payments recorded yet</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400">Amount</TableHead>
                        <TableHead className="text-slate-400">Method</TableHead>
                        <TableHead className="text-slate-400">Notes</TableHead>
                        <TableHead className="text-slate-400">Recorded By</TableHead>
                        {canManage && <TableHead className="text-slate-400"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailEntry.payments.map((p) => (
                        <TableRow key={p.id} className="border-slate-700">
                          <TableCell className="text-slate-300">{p.date}</TableCell>
                          <TableCell className="text-emerald-400">{money(p.amount)}</TableCell>
                          <TableCell className="text-slate-300 capitalize">{p.method}</TableCell>
                          <TableCell className="text-slate-300 text-xs">{p.notes || '—'}</TableCell>
                          <TableCell className="text-slate-400 text-xs">{p.recorded_by}</TableCell>
                          {canManage && (
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => reversePayment(detailEntry.id, p.id)}
                                className="border-red-700 bg-red-900/40 hover:bg-red-900/60 text-red-300 text-xs"
                                data-testid={`fa-reverse-${p.id}`}
                              >
                                Reverse
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
              <div>
                <Label className="text-slate-400">History</Label>
                <div className="space-y-2 mt-1">
                  {(detailEntry.history || []).slice().reverse().map((h, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-700/40 rounded p-2 text-xs border border-slate-700"
                    >
                      <div className="flex justify-between text-slate-400">
                        <span className="font-medium text-slate-300">{h.action}</span>
                        <span>{new Date(h.ts).toLocaleString()}</span>
                      </div>
                      <div className="text-slate-200 mt-1">{h.details}</div>
                      <div className="text-slate-500 mt-1">by {h.actor}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDetailDialog(false)}
              className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, testId }) {
  return (
    <Card className="bg-slate-800 border-slate-700" data-testid={testId}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
          {icon}
          {label}
        </div>
        <div className="text-xl sm:text-2xl font-bold text-white">{value}</div>
        {sub && <div className="text-[10px] sm:text-xs text-slate-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default FinancialAssistanceLedger;
